import './perks-post-comments.component.scss';

import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useCallback, useEffect } from 'preact/hooks';

import { SpinnerComponent } from '../../../../common/components/spinner/spinner.component';
import { Pageable } from '../../../../common/page/pageable.model';
import { useTranslation } from '../../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { buttonize } from '../../../../utils/a11y.utils';
import type { Post } from '../../../model/post.interface';
import { PerksHelper } from '../../../perks.helper';
import { replyTo } from '../../../store/perks.reducer';
import {
  selectPostComments,
  selectPostCommentsLoading,
  selectPostCommentsLoadRequestParentCommentId,
  selectPostCommentsReplyTo,
  selectUser,
} from '../../../store/perks.selectors';
import { loadPostComments } from '../../../store/perks.thunk';
import { useDisplayIdentity } from '../../../use-display-identity.hook';
import { PerksAvatar } from '../../perks-avatar/perks-avatar.component';
import { COMPACT_AVATAR_BADGE_SIZE_PX } from '../../perks-badge/perks-badge.component';
import { getMobileStack } from '../../perks-nav/mobile-stack';
import { commentCarouselSlot } from '../perks-post-info/mobile-carousel-slots';
import { findCommentById } from './comment-deep-link';
import { focusCommentInput } from './comment-entry';
import { PerksPostComment } from './perks-post-comment/perks-post-comment.component';
import { PerksPostCommentForm } from './perks-post-comment-form/perks-post-comment-form.component';

interface Props {
  post: Post;
}

export const PerksPostComments = ({ post }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const comments = useAppSelector(selectPostComments(post.id));
  const loading = useAppSelector(selectPostCommentsLoading(post.id));
  const loadParentCommentId = useAppSelector(selectPostCommentsLoadRequestParentCommentId);
  const commentReplyTo = useAppSelector(selectPostCommentsReplyTo);
  const replyToComment =
    commentReplyTo && comments?.content ? findCommentById(comments.content, commentReplyTo) : null;
  const replyToIdentity = useDisplayIdentity(replyToComment?.user);
  const user = useAppSelector(selectUser);
  const loadComments = useCallback(
    (
      pageable = Pageable.of(
        0,
        PerksHelper.COMMENTS_DEFAULT_PAGE_SIZE,
        PerksHelper.COMMENTS_DEFAULT_SORT
      )
    ) => {
      dispatch(
        loadPostComments({
          postId: post.id,
          parentCommentId: null,
          commentDepth: PerksHelper.COMMENTS_DEFAULT_DEPTH,
          childPageSize: PerksHelper.COMMENTS_CHILD_DEFAULT_PAGE_SIZE,
          pageable,
        })
      );
    },
    [dispatch, post.id]
  );

  useEffect(() => {
    // Load comments whenever the post ID changes (via loadComments dependency)
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    if (!post?.userHasAccess) return;
    document.body.setAttribute('data-mobile-comment-bar', '');
    return () => {
      document.body.removeAttribute('data-mobile-comment-bar');
    };
  }, [post?.userHasAccess]);

  const handlePage = (pageNum: number) =>
    loadComments(
      Pageable.of(
        pageNum,
        PerksHelper.COMMENTS_DEFAULT_PAGE_SIZE,
        PerksHelper.COMMENTS_DEFAULT_SORT
      )
    );

  const clearReply = (e) => {
    e.stopPropagation();
    dispatch(replyTo(null));
  };

  const handleAddComment = () => {
    dispatch(replyTo(null));
    focusCommentInput();
  };

  if (loading && !comments) {
    return (
      <section class="single-loading">
        <SpinnerComponent />
      </section>
    );
  }

  if (comments) {
    return (
      <div class="single-post-comments-wrapper">
        <div
          class={`single-desktop-comment-form${!post?.userHasAccess ? ' single-preview-form' : ''}`}
        >
          <PerksPostCommentForm post={post} displayAvatar={true} />
        </div>

        <div class="single-comment-list">
          {/* No comments case */}
          {(!comments || comments.content.length === 0) && (
            <div class="no-comments-alert">{t('perks.beFirstToComment')}</div>
          )}

          {/* Has comments */}
          {comments && comments.content.length > 0 && (
            <>
              {post?.userHasAccess ? (
                <>
                  <h2 class="single-comment-length">
                    {t(
                      post?.counts?.value?.comments === 1
                        ? 'perks.commentCountOne'
                        : 'perks.commentCount',
                      {
                        count: String(post?.counts?.value?.comments ?? 0),
                      }
                    )}
                  </h2>

                  {comments.content.map((comment, i) => (
                    <div key={comment.id} class="single-comment-container">
                      <PerksPostComment post={post} comment={comment} key={comment.id} />
                    </div>
                  ))}
                </>
              ) : (
                <div class="no-comments-alert">
                  {t(
                    post?.counts?.value?.comments === 1
                      ? 'perks.commentCountOne'
                      : 'perks.commentCount',
                    {
                      count: String(post?.counts?.value?.comments ?? 0),
                    }
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Load more button */}
        {post?.userHasAccess && !comments.last && (
          <section class="single-load-more">
            <button
              class="button btn"
              disabled={loading}
              onClick={() => handlePage(comments.number + 1)}
            >
              {!loading && !loadParentCommentId && <span>{t('perks.loadMoreReplies')}</span>}
              {loading && !loadParentCommentId && <span>{t('common.loading')}</span>}
            </button>
          </section>
        )}

        {/* Mobile-only (see scss): hands off to the fixed comment bar */}
        {post?.userHasAccess && (
          <div class="single-add-comment">
            <button class="button btn" onClick={handleAddComment}>
              {t('perks.addCommentButton')}
            </button>
          </div>
        )}

        {post?.userHasAccess &&
          (() => {
            const stack = getMobileStack();
            const commentBarEl = (
              <div class="single-mobile-comment-bar">
                {replyToComment && (
                  <div class="single-mobile-reply-header">
                    <span>{t('perks.replyingTo', { name: replyToIdentity.name })}</span>
                    <span
                      class="single-mobile-reply-close"
                      {...buttonize(clearReply, t('a11y.closeReplies'))}
                    >
                      ×
                    </span>
                  </div>
                )}
                <div class="single-mobile-input-row">
                  {user && (
                    <PerksAvatar user={user} size={36} badgeSize={COMPACT_AVATAR_BADGE_SIZE_PX} />
                  )}
                  <PerksPostCommentForm
                    post={post}
                    parentComment={replyToComment ?? undefined}
                    placeholder={replyToComment ? t('perks.replyToComment') : t('perks.addComment')}
                  />
                </div>
              </div>
            );
            const target = commentCarouselSlot.value ?? stack;
            return target ? createPortal(commentBarEl, target) : commentBarEl;
          })()}
      </div>
    );
  }

  return null;
};
